import React, { useEffect, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import { DNABox, DNAButton, DNAText } from '@alucio/lux-ui';
import InputComponent from 'src/components/Publishers/InputComponent';
import AIDisclaimer from 'src/components/Compliance/AIDisclaimer';
import { fetchPagesJson } from 'src/utils/loadCloudfrontAsset/common';
import { useComposableForm } from 'src/components/CustomFields/ComposableForm';
import { documentVersionFields } from 'src/state/forms/versioning/staticFields';

interface DNADocumentSummaryEditorProps {
  initialSummary: string;
  documentVersionId: string;
  pagesJsonKey?: string;
}

const DNADocumentSummaryEditor: React.FC<DNADocumentSummaryEditorProps> = ({
  initialSummary,
  documentVersionId,
  pagesJsonKey,
}) => {
  const { rhForm, disabled } = useComposableForm();
  const fieldName = documentVersionFields.shortSummary.id;
  const { field } = useController({ name: fieldName, control: rhForm.control });
  const { resetField, getFieldState } = rhForm;
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState<string>();
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!getFieldState(fieldName).isDirty) resetField(fieldName, { defaultValue: initialSummary });
  }, [initialSummary, fieldName, resetField, getFieldState]);

  const onReset = async () => {
    if (!pagesJsonKey || isResetting || disabled) return;
    setIsResetting(true);
    setResetError(undefined);

    try {
      const responses = await fetchPagesJson({ [documentVersionId]: pagesJsonKey }, true);
      if (!mounted.current) return;
      const originalSummary = responses.find(response => response.documentVersionId === documentVersionId)
        ?.content.shortSummary;

      if (typeof originalSummary !== 'string') {
        setResetError('The original summary is not available for this document.');
        return;
      }

      field.onChange(originalSummary);
    } catch {
      if (mounted.current) setResetError('Unable to load the original summary. Please try again.');
    } finally {
      if (mounted.current) setIsResetting(false);
    }
  };

  return (
    <DNABox appearance="col" spacing="xs">
      <InputComponent
        value={field.value?.toString() ?? ''}
        onChangeText={field.onChange}
        onBlur={field.onBlur}
        disabled={disabled || isResetting}
        characterLimit={250}
        showCharacterCounter
        required={false}
        multiline
        numOfLines={3}
        removeMarginPadding
        title="AI Summary"
      />
      <DNABox alignY="center" spacing="sm">
        <DNABox fill>
          <AIDisclaimer />
        </DNABox>
        <DNAButton
          appearance="ghost"
          status="tertiary"
          size="sm"
          onPress={onReset}
          disabled={disabled || isResetting || !pagesJsonKey}
        >
          {isResetting ? 'Resetting...' : 'Reset'}
        </DNAButton>
      </DNABox>
      {resetError && <DNAText status="danger" accessibilityRole="alert">{resetError}</DNAText>}
    </DNABox>
  );
};

export default DNADocumentSummaryEditor;
